import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { libraryAPI, favoritesAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import SubscriptionGate from '../../components/SubscriptionGate';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Heart, ChevronRight, ChevronDown, ChevronUp, Play, Clock, Brain, Apple, Video, Bookmark, X, Unlock, Lock, Target, Zap, Dumbbell, Eye } from 'lucide-react';
import { toast } from 'sonner';

import HLSPlayer from '../../components/HLSPlayer';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function videoUrl(filename) {
  if (!filename) return '';
  return filename.startsWith('videos/')
    ? `${API_URL}/api/videos/r2/${filename}`
    : `${API_URL}/api/videos/${filename}`;
}

function FavButton({ favorited, onToggle, size = 16 }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className="p-1.5 rounded-full transition-all"
      data-testid="fav-btn"
    >
      <Heart
        size={size}
        className={favorited ? 'text-red-500 fill-red-500' : 'text-[#CBD5E1]'}
        strokeWidth={2}
      />
    </button>
  );
}

export default function PlayerLibrary() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState('weeks'); // 'weeks' | 'saved' | 'unlocked'
  const [weeks, setWeeks] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [expandedWeek, setExpandedWeek] = useState(null);
  const [weekDetail, setWeekDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [favorites, setFavorites] = useState(null);
  const [unlocked, setUnlocked] = useState(null);
  const [expandedFolder, setExpandedFolder] = useState(null);
  const [loading, setLoading] = useState(true);

  const [playingVideo, setPlayingVideo] = useState(null); // drill id of currently playing video

  useEffect(() => {
    loadWeeks();
  }, []);

  const loadWeeks = async () => {
    try {
      const res = await libraryAPI.weeks();
      setWeeks(res.data.weeks || []);
      setCurrentWeek(res.data.current_week || 1);
    } catch {}
    setLoading(false);
  };

  const loadUnlocked = async () => {
    try {
      const res = await libraryAPI.unlocked();
      setUnlocked(res.data);
    } catch {}
  };

  const loadWeekDetail = async (weekNum) => {
    if (expandedWeek === weekNum) {
      setExpandedWeek(null);
      setWeekDetail(null);
      return;
    }
    setExpandedWeek(weekNum);
    setLoadingDetail(true);
    try {
      const res = await libraryAPI.weekDetail(weekNum);
      setWeekDetail(res.data);
    } catch {}
    setLoadingDetail(false);
  };

  const loadFavorites = async () => {
    try {
      const res = await favoritesAPI.list();
      setFavorites(res.data);
    } catch {}
  };

  const handleToggleFav = async (itemType, itemId, content, title) => {
    try {
      const res = await favoritesAPI.toggle({ item_type: itemType, item_id: itemId, content, title });
      if (res.data.favorited) {
        toast.success('Saved to library');
      } else {
        toast.success('Removed from library');
      }
      // Refresh current view
      if (expandedWeek) loadWeekDetail(expandedWeek);
      if (tab === 'saved') loadFavorites();
    } catch {
      toast.error('Failed to save');
    }
  };

  const switchTab = (t) => {
    setTab(t);
    if (t === 'saved' && !favorites) loadFavorites();
    if (t === 'unlocked' && !unlocked) loadUnlocked();
  };

  // Folder metadata for the Unlocked tab
  const UNLOCKED_FOLDERS = [
    { key: 'stickhandling',  label: 'Stickhandling',  icon: Zap,       color: '#2563EB' },
    { key: 'shooting',       label: 'Shooting',       icon: Target,    color: '#FF9100' },
    { key: 'off-ice',        label: 'Off-Ice',        icon: Dumbbell,  color: '#10B981' },
    { key: 'mental-tips',    label: 'Mental Tips',    icon: Brain,     color: '#A78BFA' },
    { key: 'video-review',   label: 'Video Review',   icon: Eye,       color: '#00E5FF' },
    { key: 'nutrition-tips', label: 'Nutrition Tips', icon: Apple,     color: '#F472B6' },
  ];

  return (
    <Layout title="LIBRARY" showBack>
      <SubscriptionGate>
        <div className="px-4 py-6 space-y-4">
          {/* Tab Switcher */}
          <div className="flex gap-1 bg-[#F1F5F9] rounded-lg p-1" data-testid="library-tabs">
            {[
              { id: 'weeks', label: 'Past Weeks', icon: BookOpen },
              { id: 'unlocked', label: 'Unlocked', icon: Unlock },
              { id: 'saved', label: 'Saved', icon: Heart },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => switchTab(id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors ${
                  tab === id ? 'bg-white text-[#0A2242] shadow-sm' : 'text-[#64748B]'
                }`}
                data-testid={`tab-${id}`}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>

          {/* Past Weeks Tab */}
          {tab === 'weeks' && (
            <div className="space-y-3">
              {loading ? (
                <div className="flex justify-center py-12"><div className="spinner" /></div>
              ) : weeks.length === 0 ? (
                <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
                  <BookOpen size={32} className="text-[#2563EB] mx-auto mb-3" />
                  <p className="text-[#64748B] text-sm">No training weeks available yet.</p>
                </div>
              ) : (
                weeks.map((week, i) => (
                  <motion.div
                    key={week.week_number}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <button
                      onClick={() => loadWeekDetail(week.week_number)}
                      className={`w-full text-left bg-white border rounded-xl p-4 transition-all ${
                        week.is_current ? 'border-[#2563EB]/40' : 'border-[#E2E8F0]'
                      }`}
                      data-testid={`week-${week.week_number}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-['Barlow_Condensed'] text-sm font-bold text-[#0A2242]">{week.title}</h3>
                            {week.is_current && (
                              <span className="text-[8px] bg-[#2563EB]/10 text-[#2563EB] px-2 py-0.5 rounded-full font-bold uppercase">
                                Current
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-[#64748B] mt-0.5">
                            {week.days?.length || 0} days &middot; {week.days?.reduce((s, d) => s + d.drill_count, 0) || 0} drills
                          </p>
                        </div>
                        {expandedWeek === week.week_number ? (
                          <ChevronUp size={16} className="text-[#64748B]" />
                        ) : (
                          <ChevronDown size={16} className="text-[#64748B]" />
                        )}
                      </div>
                    </button>

                    {/* Expanded Week Detail */}
                    <AnimatePresence>
                      {expandedWeek === week.week_number && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          {loadingDetail ? (
                            <div className="flex justify-center py-6"><div className="spinner" /></div>
                          ) : weekDetail ? (
                            <div className="space-y-2 mt-2">
                              {weekDetail.days?.map((day) => (
                                <div key={day.day_number} className="bg-white border border-[#E2E8F0] rounded-xl p-3 space-y-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-bold text-[#2563EB]">D{day.day_number}</span>
                                    <span className="text-xs font-bold text-[#0A2242]">{day.day_label}</span>
                                    {day.is_today && <span className="text-[8px] bg-[#10B981]/10 text-[#10B981] px-1.5 py-0.5 rounded-full font-bold">Today</span>}
                                  </div>

                                  {/* Drills */}
                                  {(day.drills || []).map((drill) => (
                                    <div key={drill.id} className="rounded-lg bg-[#F8FAFC] overflow-hidden">
                                      <div className="flex items-center gap-2 p-2">
                                        <button
                                          onClick={() => {
                                            if (drill.video_filename) {
                                              setPlayingVideo(playingVideo === drill.id ? null : drill.id);
                                            } else {
                                              navigate(`/drill/${drill.id}?week=${week.week_number}&day=${day.day_number}`);
                                            }
                                          }}
                                          className="flex-1 flex items-center gap-2 min-w-0 text-left"
                                          data-testid={`drill-play-${drill.id}`}
                                        >
                                          {drill.video_filename ? (
                                            <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                                              playingVideo === drill.id ? 'bg-[#2563EB] text-white' : 'bg-[#2563EB]/10'
                                            }`}>
                                              {playingVideo === drill.id ? (
                                                <X size={12} className="text-white" />
                                              ) : (
                                                <Play size={12} className="text-[#2563EB]" />
                                              )}
                                            </div>
                                          ) : (
                                            <div className="w-8 h-8 rounded bg-[#F1F5F9] flex items-center justify-center flex-shrink-0">
                                              <Clock size={12} className="text-[#64748B]" />
                                            </div>
                                          )}
                                          <div className="min-w-0">
                                            <p className="text-xs font-bold text-[#0A2242] truncate">{drill.title}</p>
                                            <p className="text-[9px] text-[#64748B]">{drill.category} &middot; {drill.duration_minutes}m</p>
                                          </div>
                                        </button>
                                        <FavButton
                                          favorited={drill.favorited}
                                          onToggle={() => handleToggleFav('drill', drill.id, null, drill.title)}
                                        />
                                      </div>
                                      {/* Inline Video Player */}
                                      <AnimatePresence>
                                        {playingVideo === drill.id && drill.video_filename && (
                                          <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="px-2 pb-2"
                                          >
                                            <HLSPlayer
                                              videoFilename={drill.video_filename}
                                              watermarkText={user?.name || user?.email}
                                              autoPlay
                                              className="w-full rounded-lg bg-black"
                                              data-testid={`video-player-${drill.id}`}
                                            />
                                            {drill.coaching_cues?.length > 0 && (
                                              <div className="mt-2 space-y-1">
                                                {drill.coaching_cues.map((cue, ci) => (
                                                  <p key={ci} className="text-[10px] text-[#64748B] flex items-start gap-1.5">
                                                    <span className="text-[#2563EB] font-bold mt-px">{ci + 1}.</span>
                                                    {cue}
                                                  </p>
                                                ))}
                                              </div>
                                            )}
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  ))}

                                  {/* Mental Tip */}
                                  {day.mental_tip && (
                                    <div className="flex items-start gap-2 p-2 rounded-lg bg-[#A855F7]/5 border border-[#A855F7]/10">
                                      <Brain size={14} className="text-[#A855F7] mt-0.5 flex-shrink-0" />
                                      <p className="text-[11px] text-[#0A2242] flex-1">{day.mental_tip}</p>
                                      <FavButton
                                        favorited={day.mental_tip_favorited}
                                        onToggle={() => handleToggleFav('mental_tip', `${week.week_number}-${day.day_number}`, day.mental_tip, `Week ${week.week_number} ${day.day_label}`)}
                                        size={14}
                                      />
                                    </div>
                                  )}

                                  {/* Nutrition Tip */}
                                  {day.nutrition_tip && (
                                    <div className="flex items-start gap-2 p-2 rounded-lg bg-[#10B981]/5 border border-[#10B981]/10">
                                      <Apple size={14} className="text-[#10B981] mt-0.5 flex-shrink-0" />
                                      <p className="text-[11px] text-[#0A2242] flex-1">{day.nutrition_tip}</p>
                                      <FavButton
                                        favorited={day.nutrition_tip_favorited}
                                        onToggle={() => handleToggleFav('nutrition_tip', `${week.week_number}-${day.day_number}`, day.nutrition_tip, `Week ${week.week_number} ${day.day_label}`)}
                                        size={14}
                                      />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))
              )}
            </div>
          )}

          {/* Unlocked Tab — drills the player has completed, grouped by folder */}
          {tab === 'unlocked' && (
            <div className="space-y-3">
              {!unlocked ? (
                <div className="flex justify-center py-12"><div className="spinner" /></div>
              ) : (
                <>
                  <div className="bg-[#2563EB]/5 border border-[#2563EB]/20 rounded-xl p-3">
                    <p className="text-[11px] text-[#0A2242]">
                      Drills you've already trained on. Complete more weeks to unlock more content.
                    </p>
                  </div>

                  {UNLOCKED_FOLDERS.map((folder) => {
                    const folderData = unlocked.folders?.find(f => f.key === folder.key) || { drills: [], count: 0 };
                    const count = folderData.count;
                    const isLocked = count === 0;
                    const isExpanded = expandedFolder === folder.key;
                    const Icon = folder.icon;

                    return (
                      <div key={folder.key}>
                        <button
                          onClick={() => {
                            if (isLocked) return;
                            setExpandedFolder(isExpanded ? null : folder.key);
                            setPlayingVideo(null);
                          }}
                          disabled={isLocked}
                          className={`w-full text-left rounded-xl p-4 transition-all ${
                            isLocked
                              ? 'bg-[#F8FAFC] border border-[#E2E8F0] opacity-60 cursor-not-allowed'
                              : 'bg-white border border-[#E2E8F0] hover:border-[#2563EB]/40 active:scale-[0.99]'
                          }`}
                          data-testid={`unlocked-folder-${folder.key}`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{
                                background: isLocked ? '#F1F5F9' : `${folder.color}15`,
                                border: `1px solid ${isLocked ? '#E2E8F0' : folder.color + '40'}`,
                              }}
                            >
                              {isLocked ? (
                                <Lock size={18} className="text-[#CBD5E1]" />
                              ) : (
                                <Icon size={20} style={{ color: folder.color }} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-['Barlow_Condensed'] text-base font-bold tracking-tight leading-tight">{folder.label}</h3>
                              <p className="text-[10px] text-[#64748B] uppercase tracking-widest mt-0.5">
                                {isLocked ? 'Complete drills to unlock' : `${count} ${count === 1 ? 'drill' : 'drills'} unlocked`}
                              </p>
                            </div>
                            {!isLocked && (
                              <span
                                className="font-['Barlow_Condensed'] text-2xl font-black"
                                style={{ color: folder.color }}
                              >{count}</span>
                            )}
                            {!isLocked && (isExpanded ? (
                              <ChevronUp size={16} className="text-[#64748B]" />
                            ) : (
                              <ChevronDown size={16} className="text-[#64748B]" />
                            ))}
                          </div>
                        </button>

                        <AnimatePresence>
                          {isExpanded && !isLocked && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="space-y-2 mt-2">
                                {folderData.drills.map((drill) => (
                                  <div key={drill.id} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid={`unlocked-drill-${drill.id}`}>
                                    <div className="flex items-center gap-2 p-3">
                                      <button
                                        onClick={() => {
                                          if (drill.video_filename) {
                                            setPlayingVideo(playingVideo === drill.id ? null : drill.id);
                                          } else {
                                            navigate(`/drill/${drill.id}`);
                                          }
                                        }}
                                        className="flex-1 flex items-center gap-3 min-w-0 text-left"
                                      >
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                                          playingVideo === drill.id ? 'bg-[#2563EB] text-white' : 'bg-[#2563EB]/10'
                                        }`}>
                                          {playingVideo === drill.id ? <X size={16} className="text-white" /> : <Play size={16} className="text-[#2563EB]" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-bold text-[#0A2242] truncate">{drill.title}</p>
                                          <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-[9px] text-[#64748B]">{drill.levels && drill.levels.length === 4 ? 'All Levels' : (drill.levels || []).join(', ')}</span>
                                            <span className="text-[9px] text-[#64748B]">{drill.duration_minutes}m</span>
                                          </div>
                                        </div>
                                      </button>
                                      <FavButton
                                        favorited={drill.favorited}
                                        onToggle={() => handleToggleFav('drill', drill.id, null, drill.title)}
                                      />
                                    </div>
                                    <AnimatePresence>
                                      {playingVideo === drill.id && drill.video_filename && (
                                        <motion.div
                                          initial={{ opacity: 0, height: 0 }}
                                          animate={{ opacity: 1, height: 'auto' }}
                                          exit={{ opacity: 0, height: 0 }}
                                          className="px-3 pb-3"
                                        >
                                          <HLSPlayer
                                            videoFilename={drill.video_filename}
                                            watermarkText={user?.name || user?.email}
                                            autoPlay
                                            className="w-full rounded-lg bg-black"
                                          />
                                          {drill.coaching_cues?.length > 0 && (
                                            <div className="mt-2 space-y-1">
                                              {drill.coaching_cues.map((cue, ci) => (
                                                <p key={ci} className="text-[10px] text-[#64748B] flex items-start gap-1.5">
                                                  <span className="text-[#2563EB] font-bold mt-px">{ci + 1}.</span>
                                                  {cue}
                                                </p>
                                              ))}
                                            </div>
                                          )}
                                        </motion.div>
                                      )}
                                    </AnimatePresence>
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* Saved Tab */}
          {tab === 'saved' && (
            <div className="space-y-4">
              {!favorites ? (
                <div className="flex justify-center py-12"><div className="spinner" /></div>
              ) : (
                <>
                  {/* Saved Drills */}
                  {favorites.drills?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Video size={14} className="text-[#2563EB]" />
                        <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">Saved Drills</h3>
                      </div>
                      <div className="space-y-2">
                        {favorites.drills.map((drill) => (
                          <div key={drill.id} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid={`saved-drill-${drill.id}`}>
                            <button
                              onClick={() => {
                                if (drill.video_filename) {
                                  setPlayingVideo(playingVideo === drill.id ? null : drill.id);
                                } else {
                                  navigate(`/drill/${drill.id}`);
                                }
                              }}
                              className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#F8FAFC] transition-colors"
                            >
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                                playingVideo === drill.id ? 'bg-[#2563EB] text-white' : 'bg-[#2563EB]/10'
                              }`}>
                                {playingVideo === drill.id ? <X size={16} className="text-white" /> : <Play size={16} className="text-[#2563EB]" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-[#0A2242] truncate">{drill.title}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-[9px] text-[#2563EB] font-bold uppercase">{drill.category}</span>
                                  <span className="text-[9px] text-[#64748B]">{drill.levels && drill.levels.length === 4 ? 'All Levels' : (drill.levels || []).join(', ')}</span>
                                  <span className="text-[9px] text-[#64748B]">{drill.duration_minutes}m</span>
                                </div>
                              </div>
                              <Heart size={14} className="text-red-500 fill-red-500 flex-shrink-0" />
                            </button>
                            <AnimatePresence>
                              {playingVideo === drill.id && drill.video_filename && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="px-3 pb-3"
                                >
                                  <HLSPlayer
                                    videoFilename={drill.video_filename}
                                    watermarkText={user?.name || user?.email}
                                    autoPlay
                                    className="w-full rounded-lg bg-black"
                                  />
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Saved Mental Tips */}
                  {favorites.mental_tips?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Brain size={14} className="text-[#A855F7]" />
                        <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">Saved Mental Tips</h3>
                      </div>
                      <div className="space-y-2">
                        {favorites.mental_tips.map((tip) => (
                          <div
                            key={tip.item_id}
                            className="bg-white border border-[#A855F7]/15 rounded-xl p-3"
                            data-testid={`saved-mental-${tip.item_id}`}
                          >
                            <p className="text-[10px] text-[#A855F7] font-bold uppercase tracking-wider mb-1">{tip.title}</p>
                            <p className="text-xs text-[#0A2242]">{tip.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Saved Nutrition Tips */}
                  {favorites.nutrition_tips?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Apple size={14} className="text-[#10B981]" />
                        <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">Saved Nutrition Tips</h3>
                      </div>
                      <div className="space-y-2">
                        {favorites.nutrition_tips.map((tip) => (
                          <div
                            key={tip.item_id}
                            className="bg-white border border-[#10B981]/15 rounded-xl p-3"
                            data-testid={`saved-nutrition-${tip.item_id}`}
                          >
                            <p className="text-[10px] text-[#10B981] font-bold uppercase tracking-wider mb-1">{tip.title}</p>
                            <p className="text-xs text-[#0A2242]">{tip.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty state */}
                  {!favorites.drills?.length && !favorites.mental_tips?.length && !favorites.nutrition_tips?.length && (
                    <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
                      <Heart size={32} className="text-[#CBD5E1] mx-auto mb-3" />
                      <p className="text-[#64748B] text-sm font-medium">No saved items yet</p>
                      <p className="text-[#94A3B8] text-xs mt-1">Tap the heart on any drill, mental tip, or nutrition tip to save it here.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </SubscriptionGate>
    </Layout>
  );
}
